import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DollarSign,
  TrendingUp,
  Wallet,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Users,
} from 'lucide-react';
import { AgentHeader } from '../../components/layout/AgentHeader';
import { AgentSidebar } from '../../components/layout/AgentSidebar';
import { BottomNav } from '../../components/layout/BottomNav';
import { useAgentStore } from '../../store/agentStore';
import { agentApi } from '../../api/agent.api';
import { cashTransactionsApi } from '../../api/cash-transactions.api';
import { reportsApi } from '../../api/reports.api';
import { FloatMonitor } from '../../components/features/FloatMonitor';
import { DailyReportCard } from '../../components/features/DailyReportCard';
import { RecentTransactions } from '../../components/features/RecentTransactions';
import { QuickActions } from '../../components/features/QuickActions';
import { PerformanceCard } from '../../components/features/PerformanceCard';
import { TipsCard } from '../../components/features/TipsCard';
import { clsx } from 'clsx';

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  trend?: { value: string; isPositive: boolean };
  bgColor: string;
  iconBg: string;
}

function StatCard({ title, value, icon, trend, bgColor, iconBg }: StatCardProps) {
  return (
    <div className={clsx('rounded-lg p-6 border border-gray-200', bgColor)}>
      <div className="flex items-center justify-between mb-4">
        <div className={clsx('p-3 rounded-lg', iconBg)}>{icon}</div>
        {trend && (
          <div
            className={clsx(
              'flex items-center gap-1 text-sm font-medium',
              trend.isPositive ? 'text-green-600' : 'text-red-600'
            )}
          >
            {trend.isPositive ? (
              <ArrowUpRight className="w-4 h-4" />
            ) : (
              <ArrowDownRight className="w-4 h-4" />
            )}
            {trend.value}
          </div>
        )}
      </div>
      <div>
        <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  );
}

interface AlertCardProps {
  type: 'warning' | 'error' | 'info';
  title: string;
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

function AlertCard({ type, title, message, action }: AlertCardProps) {
  const colorClasses = {
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className={clsx('rounded-lg border p-4', colorClasses[type])}>
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="font-semibold mb-1">{title}</h3>
          <p className="text-sm mb-3">{message}</p>
          {action && (
            <button
              onClick={action.onClick}
              className="text-sm font-medium underline hover:no-underline"
            >
              {action.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50 animate-pulse">
      <div className="h-20 bg-white border-b border-gray-200" />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="h-40 bg-white rounded-lg mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-white rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AgentDashboardPage() {
  const { cashFloat, setCashFloat } = useAgentStore();

  // Query para obtener perfil completo
  const { data: profile, isLoading } = useQuery({
    queryKey: ['agentProfile'],
    queryFn: agentApi.getProfile,
    refetchInterval: 60000, // Refetch cada 60 segundos
  });

  // Query para transacciones recientes
  const { data: recentTransactions } = useQuery({
    queryKey: ['recentTransactions'],
    queryFn: () => cashTransactionsApi.getAll({ page: 1, limit: 5 }),
  });

  // Query para estadísticas del día
  const { data: todayStats, error: todayStatsError } = useQuery({
    queryKey: ['todayStats'],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      const data = await reportsApi.getDailyReport(today);
      return data;
    },
    retry: false, // No reintentar automáticamente para ver el error
  });

  // Query de rendimiento mensual — endpoint /agent/me/dashboard/performance
  const now = new Date();
  const { data: perfDashboard } = useQuery({
    queryKey: ['performanceDashboard', now.getMonth() + 1, now.getFullYear()],
    queryFn: () => reportsApi.getPerformanceDashboard(now.getMonth() + 1, now.getFullYear()),
    staleTime: 5 * 60 * 1000, // 5 min
    retry: false,
    throwOnError: false,
  });

  useEffect(() => {
    // todayStatsError handled by component rendering
  }, [todayStatsError]);

  useEffect(() => {
    if (profile?.cashFloat) {
      setCashFloat(profile.cashFloat);
    }
  }, [profile, setCashFloat]);

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  const stats = profile?.statistics || {
    totalEarnings: 0,
    pendingCommissions: 0,
    monthlyVolume: 0,
    monthlyTransactions: 0,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AgentHeader />

      <div className="flex">
        <AgentSidebar />

        <main className="flex-1 pb-20 lg:pb-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Float Monitor - Prominente */}
            <div className="mb-6">
              <FloatMonitor cashFloat={cashFloat} className="shadow-lg" />
            </div>

            {/* Quick Actions */}
            <div className="mb-6">
              <QuickActions />
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {/* Comisiones del Mes */}
              <StatCard
                title="Comisiones (Mes)"
                value={`$${(perfDashboard?.performance?.monthly?.commissions ?? stats.totalEarnings).toLocaleString()}`}
                icon={<DollarSign className="w-6 h-6 text-yellow-600" />}
                trend={
                  perfDashboard?.performance?.commissionChange !== undefined
                    ? {
                        value: `${Math.abs(perfDashboard.performance.commissionChange).toFixed(0)}%`,
                        isPositive: perfDashboard.performance.commissionChange >= 0,
                      }
                    : undefined
                }
                bgColor="bg-yellow-50"
                iconBg="bg-yellow-100"
              />

              {/* Volumen Mensual */}
              <StatCard
                title="Volumen Mensual"
                value={`$${(perfDashboard?.performance?.monthly?.totalVolume ?? stats.monthlyVolume).toLocaleString()}`}
                icon={<Wallet className="w-6 h-6 text-blue-600" />}
                bgColor="bg-blue-50"
                iconBg="bg-blue-100"
              />

              {/* Transacciones del mes */}
              <StatCard
                title="Transacciones (Mes)"
                value={(perfDashboard?.performance?.monthly?.transactionCount ?? stats.monthlyTransactions).toString()}
                icon={<Users className="w-6 h-6 text-purple-600" />}
                bgColor="bg-purple-50"
                iconBg="bg-purple-100"
              />

              {/* Promedio por Tx */}
              <StatCard
                title="Promedio por Tx"
                value={`$${(perfDashboard?.performance?.avgTransactionSize ?? 0).toLocaleString()}`}
                icon={<TrendingUp className="w-6 h-6 text-green-600" />}
                bgColor="bg-green-50"
                iconBg="bg-green-100"
              />
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Transactions */}
              <div className="lg:col-span-2 space-y-6">
                {/* Today's Activity */}
                {todayStats && <DailyReportCard report={todayStats as unknown as Parameters<typeof DailyReportCard>[0]['report']} />}

                {/* Recent Transactions */}
                <div className="bg-white rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Transacciones Recientes
                    </h2>
                    <a
                      href="/transactions"
                      className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                    >
                      Ver historial →
                    </a>
                  </div>
                  <RecentTransactions transactions={recentTransactions?.data || []} />
                </div>
              </div>

              {/* Right Column - Alerts & Info */}
              <div className="space-y-6">
                {/* Alerts */}
                {cashFloat?.status === 'LOW' && (
                  <AlertCard
                    type="warning"
                    title="Liquidez Baja"
                    message="Tu efectivo está por debajo del umbral. Considera solicitar una recarga."
                    action={{
                      label: 'Solicitar Recarga',
                      onClick: () => (window.location.href = '/float/request'),
                    }}
                  />
                )}

                {cashFloat?.status === 'CRITICAL' && (
                  <AlertCard
                    type="error"
                    title="¡Liquidez Crítica!"
                    message="Tu efectivo está muy bajo. Recarga urgente necesaria."
                    action={{
                      label: 'Recarga Urgente',
                      onClick: () => (window.location.href = '/float/request'),
                    }}
                  />
                )}

                {/* Performance Card */}
                <PerformanceCard perf={perfDashboard} />

                {/* Tips Card */}
                <TipsCard />
              </div>
            </div>
          </div>
        </main>
      </div>

      <BottomNav />
    </div>
  );
}
